import {
  AtomErrors,
  AuthErrors,
  InternalErrors,
  PrismaErrors,
  UserServiceErrors,
} from "@atoms/atom-types";
import type { NotifyLevel } from "./notifier";

export type ErrorClassDisplay = Readonly<{
  level: NotifyLevel;
  /** Why the request failed, in one sentence. German is the source text. */
  messageKey: string;
  messageDefault: string;
  /** Whether the reference of the failed request is shown as a line. */
  showReference: boolean;
}>;

/**
 * Level and sentence per error class the backend sends (`errorClass`). Keyed by
 * the wire value; a class that is not listed here has no sentence.
 */
const errorClassDisplays: Readonly<Record<string, ErrorClassDisplay>> = {
  permission: {
    level: "warning",
    messageKey: "framework.errors.class.permission",
    messageDefault: "Dafür fehlt Ihnen die Berechtigung.",
    showReference: false,
  },
  validation: {
    level: "warning",
    messageKey: "framework.errors.class.validation",
    messageDefault:
      "Die Angaben sind nicht gültig. Bitte prüfen Sie Ihre Eingabe.",
    showReference: false,
  },
  conflict: {
    level: "warning",
    messageKey: "framework.errors.class.conflict",
    messageDefault:
      "Die Daten wurden inzwischen geändert. Bitte prüfen Sie den aktuellen Stand und versuchen Sie es erneut.",
    showReference: false,
  },
  busy: {
    level: "warning",
    messageKey: "framework.errors.class.busy",
    messageDefault:
      "Die Daten werden gerade von einem anderen Vorgang bearbeitet. Bitte versuchen Sie es gleich noch einmal.",
    showReference: false,
  },
  "not-found": {
    level: "warning",
    messageKey: "framework.errors.class.notFound",
    messageDefault:
      "Der Eintrag wurde nicht gefunden oder ist für Sie nicht sichtbar.",
    showReference: false,
  },
  rule: {
    level: "warning",
    messageKey: "framework.errors.class.rule",
    messageDefault: "Diese Aktion ist im aktuellen Zustand nicht möglich.",
    showReference: false,
  },
  unavailable: {
    level: "error",
    messageKey: "framework.errors.class.unavailable",
    messageDefault:
      "Ein benötigter Dienst ist gerade nicht erreichbar. Bitte versuchen Sie es später erneut.",
    showReference: true,
  },
  auth: {
    level: "warning",
    messageKey: "framework.errors.class.auth",
    messageDefault: "Für diese Aktion ist eine gültige Anmeldung erforderlich.",
    showReference: false,
  },
  internal: {
    level: "error",
    messageKey: "framework.errors.class.internal",
    messageDefault:
      "Ein unerwarteter Fehler ist aufgetreten. Tritt er erneut auf, melden Sie ihn bitte mit der Referenz.",
    showReference: true,
  },
};

export function getErrorClassDisplay(
  errorClass: string,
): ErrorClassDisplay | undefined {
  return Object.hasOwn(errorClassDisplays, errorClass)
    ? errorClassDisplays[errorClass]
    : undefined;
}

/** `framework/internal/other` → `framework/`; a code without `/` is kept. */
function getCodeFamilyPrefix(code: string): string {
  const separator = code.indexOf("/");
  return separator === -1 ? code : code.slice(0, separator + 1);
}

/** Code of a rejected GraphQL operation or reducer input: `ERR-VAL-001`. */
const VALIDATION_CODE_PREFIX = "ERR-VAL-";

const frameworkCodePrefixes: readonly string[] = [
  ...new Set(
    [AtomErrors, AuthErrors, InternalErrors, PrismaErrors, UserServiceErrors]
      .flatMap((codes) => Object.values<string>(codes))
      .map(getCodeFamilyPrefix),
  ),
  VALIDATION_CODE_PREFIX,
];

/**
 * Whether a code is one a project defines itself (`BPV_…`): it belongs to
 * none of the framework's code families.
 */
export function isProjectErrorCode(code: string): boolean {
  return !frameworkCodePrefixes.some((prefix) => code.startsWith(prefix));
}
